import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateIntegrationBody, UpdateIntegrationBody } from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  createDemoIntegration,
  disableDemoIntegration,
  listDemoIntegrations,
  updateDemoIntegration,
} from "./demo-integrations.js";
import { queryKeys } from "./query-keys.js";

export function useIntegrations() {
  const { session, isDemoMode } = useSession();
  const tenantId = session?.companyId ?? "demo-tenant";

  return useQuery({
    queryKey: queryKeys.integrations.list(tenantId),
    queryFn: async () => {
      if (isDemoMode) return { data: listDemoIntegrations(tenantId) };
      return getApiClient().listIntegrations();
    },
  });
}

export function useCreateIntegration() {
  const { session, isDemoMode } = useSession();
  const tenantId = session?.companyId ?? "demo-tenant";
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateIntegrationBody) => {
      if (isDemoMode) return createDemoIntegration(tenantId, body);
      return getApiClient().createIntegration(body, crypto.randomUUID());
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
    },
  });
}

export function useUpdateIntegration() {
  const { session, isDemoMode } = useSession();
  const tenantId = session?.companyId ?? "demo-tenant";
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; body: UpdateIntegrationBody }) => {
      if (isDemoMode) {
        return updateDemoIntegration(tenantId, input.id, input.body);
      }
      return getApiClient().updateIntegration(input.id, input.body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
    },
  });
}

export function useDisableIntegration() {
  const { session, isDemoMode } = useSession();
  const tenantId = session?.companyId ?? "demo-tenant";
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (isDemoMode) return disableDemoIntegration(tenantId, id);
      return getApiClient().disableIntegration(id);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
    },
  });
}
